import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, HelpCircle, Send, Sparkles } from "lucide-react";

import { useAskAssistant, useAssistantSuggestions } from "@/api/queries";
import type { AssistantAnswer, AssistantCertainty } from "@/api/types";
import { Button } from "@/components/Button";
import { StatusBadge } from "@/components/StatusBadge";
import type { BadgeTone } from "@/components/StatusBadge";

const CERTAINTY: Record<AssistantCertainty, { label: string; tone: BadgeTone }> = {
  CERTAIN: { label: "Certain", tone: "available" },
  NEEDS_CHECK: { label: "À vérifier", tone: "reserved" },
  AMBIGUOUS: { label: "Ambigu", tone: "warning" },
  FAILED: { label: "Sans réponse", tone: "unknown" },
};

/**
 * « Demander à HD-Vault » (Frontend Spec §23, PHASE 11).
 *
 * A quick-access layer, explicitly not a replacement for the screens: every
 * answer ends in links to them.
 *
 * Two things it deliberately does not do. It does not stream a conversation —
 * one question, one answer, and the answer stays on screen until the next
 * question. And it does not act: the buttons under an answer are routes to the
 * screen that owns the rule, where the confirmation and the audit trail already
 * live.
 */
export function AskHdVault() {
  const [question, setQuestion] = useState("");
  const ask = useAskAssistant();
  const { data: suggestions } = useAssistantSuggestions();
  const answer: AssistantAnswer | undefined = ask.data;

  function submit(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    setQuestion(trimmed);
    ask.mutate(trimmed);
  }

  return (
    <section
      className="rounded-lg border border-brand/40 bg-brand-subtle/40 p-3 sm:p-4"
      aria-label="Demander à HD-Vault"
    >
      <div className="flex items-start gap-3">
        <span className="hidden size-10 shrink-0 items-center justify-center rounded-md bg-brand/15 text-brand sm:flex">
          <Sparkles className="size-5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-ink">Demander à HD-Vault…</p>

          <form
            className="mt-2 flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              submit(question);
            }}
          >
            <input
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Qui a la perceuse ? Combien il reste de connecteurs ?"
              aria-label="Votre question"
              className="h-10 min-w-0 flex-1 rounded-md border border-border bg-surface-sunken px-3 text-sm text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none"
            />
            <Button
              variant="primary"
              type="submit"
              disabled={!question.trim() || ask.isPending}
              aria-label="Demander"
            >
              <Send className="size-4" aria-hidden />
            </Button>
          </form>

          {!answer && !ask.isPending && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {suggestions?.questions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => submit(item)}
                  className="rounded-full border border-border bg-surface px-2.5 py-1 text-[12px] text-ink-muted transition-colors hover:border-brand/50 hover:text-ink"
                >
                  {item}
                </button>
              ))}
            </div>
          )}

          {ask.isPending && (
            <p className="mt-3 text-[13px] text-ink-subtle">Je regarde…</p>
          )}

          {ask.error && (
            <p className="mt-3 flex items-center gap-2 text-[13px] text-status-out">
              <AlertCircle className="size-4 shrink-0" aria-hidden />
              {String(ask.error)}
            </p>
          )}

          {answer && !ask.isPending && <AnswerPanel answer={answer} />}
        </div>
      </div>
    </section>
  );
}

function AnswerPanel({ answer }: { answer: AssistantAnswer }) {
  const certainty = CERTAINTY[answer.certainty];

  return (
    <div className="mt-3 rounded-md border border-border bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 text-[14px] font-medium text-ink">{answer.headline}</p>
        {/* Four words, never a percentage: a confidence decimal next to every
            answer trains people to stop reading it. */}
        <StatusBadge tone={certainty.tone} className="shrink-0">
          {certainty.label}
        </StatusBadge>
      </div>

      {answer.facts.length > 0 && (
        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-[13px] @2xl:grid-cols-2">
          {answer.facts.map((fact) => (
            <div key={`${fact.label}-${fact.value}`} className="flex justify-between gap-3">
              <dt className="text-ink-subtle">{fact.label}</dt>
              <dd
                className={`tnum truncate text-right ${
                  fact.emphasis ? "font-medium text-ink" : "text-ink-muted"
                }`}
              >
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {answer.alternatives.length > 0 && (
        <ul className="mt-2 flex flex-col divide-y divide-border/60">
          {answer.alternatives.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="flex items-center gap-2 py-2 text-[13px] text-ink hover:text-brand"
              >
                <ArrowRight className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {item.hint && (
                  <span className="shrink-0 truncate text-[11px] text-ink-subtle">
                    {item.hint}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {answer.explanation && (
        <p className="mt-2 flex items-start gap-2 text-[12px] text-ink-subtle">
          <HelpCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {answer.explanation}
        </p>
      )}

      {answer.actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {answer.actions.map((action) => (
            <Link
              key={`${action.label}-${action.to}`}
              to={action.to}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface-raised px-3 text-[13px] text-ink transition-colors hover:border-brand/50 hover:text-brand"
            >
              {action.label}
              <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
